import * as React from "react";
import { Link, useNavigate } from "react-router";
import { ArrowLeft, CirclePlay, Settings2 } from "lucide-react";

import SurahName from "@/components/common/SurahName";
import { useMediaQuery } from "@/lib/use-media-query";
import { cn, surahsNamesMap } from "@/lib/utils";
import { useColorsSettings } from "@/store/settings";
import type { Video } from "@/lib/types";

import { AspectRatio } from "./ui/aspect-ratio";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { Switch } from "./ui/switch";

interface QuranPageNavProps {
  video: Video | null;
  pageNumber: number;
  /** Surah the page opens in; the context is left out while it is unknown. */
  surahNumber?: number | null;
  juz?: number;
}

const iconButton =
  "pressable grid size-10 place-items-center rounded-full text-ink-muted hover:bg-paper-200 hover:text-ink";
const sectionLabel =
  "mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-subtle";
const row =
  "flex min-h-12 items-center justify-between gap-4 rounded-control bg-paper-100 px-4 py-3 ring-1 ring-paper-300/70";

interface VideoDialogProps {
  /** Row label in the settings sheet. */
  label: string;
  /** Dialog title, also the iframe's accessible title. */
  title: string;
  /** YouTube video id. */
  videoId: string;
}

const VideoDialog: React.FC<VideoDialogProps> = ({ label, title, videoId }) => (
  <div className={row}>
    <p className="text-sm font-medium text-ink">{label}</p>
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-10 rounded-full px-3.5"
          aria-label={`Pogledaj ${label.toLowerCase()}`}
        >
          Pogledaj
          <CirclePlay className="text-emerald-800" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0">
        <DialogHeader className="min-h-15 justify-center border-b border-paper-300/70 px-5 py-3 pr-14">
          <DialogTitle className="text-sm sm:text-base">{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Video objašnjenje za ovu stranicu.
          </DialogDescription>
        </DialogHeader>
        <AspectRatio ratio={16 / 9} className="bg-ink">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title={title}
            loading="lazy"
            className="size-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </AspectRatio>
      </DialogContent>
    </Dialog>
  </div>
);

const QuranPageNav: React.FC<QuranPageNavProps> = ({
  video,
  pageNumber,
  surahNumber,
  juz,
}) => {
  const navigate = useNavigate();
  const { colors, toggleColors } = useColorsSettings();
  // Bottom sheet on phones (controls in the thumb zone), side sheet from md.
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const linksTitleId = React.useId();
  const linksHintId = React.useId();

  const goBack = () => {
    // A reader opened straight from a link has no in-app history to return to.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate("/");
  };

  const hifzVideo = video?.hifz_youtube_url;
  const tajweedVideo = video?.tajweed_youtube_url;

  return (
    <header className="px-3 pt-[max(0.5rem,env(safe-area-inset-top))] print:hidden">
      <nav className="mx-auto flex h-12 max-w-[40rem] items-center justify-between gap-3 rounded-full bg-paper-50/80 pl-4 pr-1 shadow-card ring-1 ring-paper-300/80 backdrop-blur-md">
        <Link
          to="/"
          className="pressable inline-flex h-10 shrink-0 select-none items-center rounded-full text-[17px] font-bold tracking-tight"
        >
          <span className="text-emerald-900">hifz</span>
          <span className="font-medium text-emerald-600">academy</span>
        </Link>

        {surahNumber ? (
          <div className="hidden min-w-0 items-center gap-2 text-xs text-ink-muted sm:flex">
            {/* The visible transliteration names the surah for screen readers. */}
            <span aria-hidden className="shrink-0">
              <SurahName number={surahNumber} className="text-[22px]" />
            </span>
            <span className="truncate font-medium text-ink">
              {surahsNamesMap.get(surahNumber)}
            </span>
            {juz ? (
              <>
                <span aria-hidden>·</span>
                <span className="shrink-0 whitespace-nowrap tabular-nums">
                  {juz}. džuz
                </span>
              </>
            ) : null}
          </div>
        ) : null}

        <div className="flex shrink-0 items-center">
          <button
            type="button"
            aria-label="Nazad"
            onClick={goBack}
            className={iconButton}
          >
            <ArrowLeft className="size-[18px]" />
          </button>
          <Sheet>
            <SheetTrigger asChild>
              <button type="button" aria-label="Podešenja" className={iconButton}>
                <Settings2 className="size-[18px]" />
              </button>
            </SheetTrigger>
            <SheetContent side={isDesktop ? "right" : "bottom"} className="p-0">
              {/* On the bottom sheet the grab handle already adds top space;
                  pt-1 lines the title up with the close button. */}
              <SheetHeader className={cn("pr-14", !isDesktop && "pb-4 pt-1")}>
                <SheetTitle className="flex items-center gap-2">
                  <Settings2 className="size-4 text-emerald-800" />
                  Podešenja
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Podešenja za aplikaciju.
                </SheetDescription>
              </SheetHeader>

              <div
                className={cn(
                  "min-h-0 space-y-6 overflow-y-auto px-5 py-5",
                  // The bottom sheet pads the safe area itself.
                  isDesktop ? "pb-[max(1.5rem,env(safe-area-inset-bottom))]" : "pb-6",
                )}
              >
                <section>
                  <h3 className={sectionLabel}>Općenito</h3>
                  <label
                    className={cn(
                      row,
                      "cursor-pointer transition-colors duration-150 hover:bg-paper-200/60",
                    )}
                  >
                    <span className="min-w-0">
                      <span
                        id={linksTitleId}
                        className="block text-sm font-medium text-ink"
                      >
                        Poveznice
                      </span>
                      <span id={linksHintId} className="block text-xs text-ink-muted">
                        Boje i oblici na riječima
                      </span>
                    </span>
                    <Switch
                      checked={colors}
                      onCheckedChange={toggleColors}
                      aria-labelledby={linksTitleId}
                      aria-describedby={linksHintId}
                    />
                  </label>
                </section>

                <section>
                  <h3 className={sectionLabel}>Dodatne informacije</h3>
                  {hifzVideo || tajweedVideo ? (
                    <div className="space-y-2">
                      {hifzVideo ? (
                        <VideoDialog
                          label="Objašnjenje"
                          title={`Hifz objašnjenje - stranica broj ${pageNumber}`}
                          videoId={hifzVideo}
                        />
                      ) : null}
                      {tajweedVideo ? (
                        <VideoDialog
                          label="Tedžvid"
                          title={`Tedžvid objašnjenje - stranica broj ${pageNumber}`}
                          videoId={tajweedVideo}
                        />
                      ) : null}
                    </div>
                  ) : (
                    <p className="rounded-control bg-paper-100 px-4 py-3 text-sm text-ink-muted ring-1 ring-paper-300/70">
                      Za ovu stranicu nema video objašnjenja.
                    </p>
                  )}
                </section>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default QuranPageNav;
